import React, { useEffect, useState, useMemo } from "react";
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip } from "recharts";
import api from "../lib/api";

// Stable references — extracted to module scope so they don't cause re-renders.
const CHART_MARGIN = { top: 10, right: 10, bottom: 0, left: 0 };
const Y_DOMAIN = ["dataMin", "dataMax"];
const Y_TICK = { fill: "#525252", fontSize: 10, fontFamily: "JetBrains Mono" };
const Y_AXIS_LINE = { stroke: "#222" };
const TOOLTIP_CONTENT_STYLE = {
  background: "#121212",
  border: "1px solid #222",
  fontFamily: "JetBrains Mono",
  fontSize: 12,
};
const TOOLTIP_LABEL_STYLE = { color: "#A3A3A3" };

const tooltipFormatter = (value) => [value.toFixed(value < 1 ? 5 : 2), "Price"];
const emptyLabelFormatter = () => "";

export default function PriceChart({ symbol, height = 280, stroke }) {
  const [points, setPoints] = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await api.get(`/markets/${symbol}/history`);
        if (!cancelled) setPoints(r.data.points || []);
      } catch { /* ignore transient refresh failures */ }
    }
    load();
    const id = setInterval(load, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [symbol]);

  const data = useMemo(() => points.map((p) => ({ t: p.t, p: p.p })), [points]);
  const color = useMemo(() => {
    if (stroke) return stroke;
    const first = data[0]?.p ?? 0;
    const last = data[data.length - 1]?.p ?? 0;
    return last >= first ? "#00E676" : "#FF3B30";
  }, [stroke, data]);

  return (
    <div className="w-full" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={CHART_MARGIN}>
          <YAxis
            domain={Y_DOMAIN}
            tick={Y_TICK}
            axisLine={Y_AXIS_LINE}
            tickLine={false}
            width={60}
          />
          <Tooltip
            contentStyle={TOOLTIP_CONTENT_STYLE}
            labelStyle={TOOLTIP_LABEL_STYLE}
            formatter={tooltipFormatter}
            labelFormatter={emptyLabelFormatter}
          />
          <Line
            type="monotone"
            dataKey="p"
            stroke={color}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
